<template>
  <div v-if="event" class="flex flex-col gap-2">
    <div
      :id="`event-${event.id}`"
      :data-tint="event.tint"
      class="border-primary flex flex-shrink-0 flex-row items-center rounded-md border"
      :data-event-id="event.id"
    >
      <div
        class="bg-primary text-contrast-primary flex h-full flex-shrink-0 items-center justify-center px-3 text-xl font-medium"
      >
        {{ $t(`enums.event.type.${event.type}`) }}
      </div>
      <div class="capitalize-first px-2 py-2 text-lg font-bold">
        {{ dateFrom }} {{ formatedEventTime }}
      </div>
    </div>
    <div>
      <h3 class="text-2xl font-bold">
        {{
          event.title ??
          $t(`enums.event.type.${event.extendedProps?.type}`) +
            ' - ' +
            event?.extendedProps?.full_name ??
          ''
        }}
      </h3>
    </div>
    <div class="flex flex-col">
      <section :class="sectionClass">
        <h3 :class="sectionTitleClass">{{ $t('attributes.place') }}</h3>
        <div v-if="event.place_model" class="flex flex-row">
          <div class="flex-1 text-sm text-gray-500">
            <span class="justify-self-start text-base text-gray-800">
              {{ event.place_model?.name }}
            </span>
            <p class="text-gray-600">
              {{ event.place_model?.street }}
              <br />
              {{ event.place_model?.zip }} {{ event.place_model?.city }}
            </p>
          </div>
          <div class="flex-shrink-0">
            <button
              type="button"
              class="rounded-md border px-2 py-1"
              @click="showMap = !showMap"
            >
              <FontAwesomeIcon size="lg" :icon="['fal', 'map-marker-alt']" />
            </button>
          </div>
        </div>
        <div v-if="showMap">
          <iframe
            class="h-96 w-full rounded-md border border-slate-300"
            :src="`https://www.google.com/maps/embed/v1/place?q=${event.place_model?.street},+${event.place_model?.city},+${event.place_model.country}&key=${googleKey}`"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </section>

      <section v-if="event.dossier?.responsable" :class="sectionClass">
        <div class="flex flex-row">
          <AvatarComponent
            :user_id="event.dossier?.responsable_id"
            size="10"
            :badge="true"
            :subtitle="$t('attributes.responsable')"
          />
        </div>
      </section>

      <template
        v-if="
          event.assignedPeopleCollection &&
          event.assignedPeopleCollection?.length > 0
        "
      >
        <section :class="sectionClass">
          <h3 :class="sectionTitleClass">
            {{ $t('global.assigned_persons') }}
          </h3>
          <div class="flex flex-row flex-wrap gap-2">
            <template
              v-for="assigned_person in event.assignedPeopleCollection"
              :key="assigned_person.id"
            >
              <AvatarComponent
                :user_id="assigned_person.id"
                size="12"
                :badge="true"
              />
            </template>
          </div>
        </section>
      </template>
    </div>
    <div
      v-if="
        event.type === 'COC' &&
        event?.properties?.coffin_closing_with_sealing_process
      "
      class="gap-x-2"
    >
      <span class="font-bold uppercase">
        {{ $t('attributes.coffin_closing_with_sealing_process') + ' ' }}
      </span>
      <span
        v-if="event?.properties?.coffin_closing_sealing_process_time"
        class="font-medium"
      >
        {{
          `${$t('global.at_time')} ${event?.properties?.coffin_closing_sealing_process_time} `
        }}
      </span>
      <span
        v-if="event?.properties?.coffin_closing_sealing_process_by"
        class="font-medium"
      >
        {{
          `${$t('global.by')} ${sealingProcessOptions.find((type) => type.id === event?.properties?.coffin_closing_sealing_process_by)?.name}`
        }}
      </span>
    </div>
    <div
      v-if="event.type === EventType.RECEPTION && event?.properties?.reception_catering_formula"
    >
      <h3 :class="sectionTitleClass">{{ $t('attributes.reception_catering_formula') }}</h3>
      <div>{{ event.properties.reception_catering_formula }}</div>
    </div>
    <div v-if="event.remarks">
      <h3 :class="sectionTitleClass">{{ $t('attributes.remarks') }}</h3>
      <div>{{ event.remarks }}</div>
    </div>
    <div v-if="event.internal_note">
      <h3 :class="sectionTitleClass">{{ $t('attributes.internal_note') }}</h3>
      <div>{{ event.internal_note }}</div>
    </div>

    <slot name="dossier-summary" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'

import AvatarComponent from '@c/AvatarComponent'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { EventType } from '@/assets/enums'

export default {
  name: 'DisplayEventModal',
  components: { FontAwesomeIcon, AvatarComponent },
  data() {
    return {
      EventType: EventType,
      showMap: false,
      sealingProcessOptions: [
        { id: 'FH', name: this.$t('attributes.the_funeral_home') },
        { id: 'POL', name: this.$t('attributes.the_policy') }
      ]
    }
  },
  computed: {
    ...mapGetters({
      event: 'agenda/getEvent'
    }),
    formatedEventTime() {
      let from = moment(this.event.date_from).format('HH:mm')
      let to = moment(this.event.date_to)
      return to.isValid() ? `${from} - ${to.format('HH:mm')}` : `${from}`
    },
    dateFrom() {
      if (!this.event.date_from) {
        return null
      }
      return this.$h.formatDate(
        this.event.date_from,
        'dddd D MMMM',
        this.$i18n.locale.substring(0, 2)
      )
    },
    sectionTitleClass() {
      return 'text-lg font-semi-bold font-medium capitalize-first'
    },
    sectionClass() {
      return 'flex flex-col py-2'
    },
    googleKey() {
      return import.meta.env.VUE_APP_GOOGLE_MAP_API_KEY
    }
  }
}
</script>
